import { Button } from "@/components/ui/button";
import { getRandomHymns, Hymn } from "@/services/hymnsApi";
import { getHymnSlug, getLanguageLabel, scrollToTop } from "@/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionBackgroundVideo from "./videos/SectionBackgroundVideo";

const HomeGarizimSections = () => {
  const navigate = useNavigate();
  const [randomHymns, setRandomHymns] = useState<Hymn[]>([]);
  const [loadingRandom, setLoadingRandom] = useState<boolean>(true);
  const [hasErrorRandom, setHasErrorRandom] = useState<boolean>(false);

  const fetchRandom = async () => {
    setLoadingRandom(true);
    setHasErrorRandom(false);
    try {
      const songs = await getRandomHymns(4);
      setRandomHymns(songs);
    } catch (err) {
      console.error("Failed to load random songs:", err);
      setHasErrorRandom(true);
    } finally {
      setLoadingRandom(false);
    }
  };

  useEffect(() => {
    fetchRandom();
  }, []);

  const handleRouteChange = (route: string) => {
    scrollToTop();
    navigate(route);
  };

  return (
    <div className="bg-gray-50">
      {/* Spiritual Heritage & Living Faith Section */}
      <section className="py-10 sm:py-20 lg:py-28 bg-white relative overflow-hidden">
        <SectionBackgroundVideo />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-2xl">
            <div className="bg-white/95 backdrop-blur-md rounded-3xl p-5 sm:p-8 lg:p-12 border border-slate-200/80 shadow-2xl">
              <span className="inline-block px-3 py-1 rounded-full bg-amber-50 border border-amber-200/80 text-amber-800 text-[11px] sm:text-xs font-bold uppercase tracking-widest mb-3 sm:mb-4">
                Héritage & Vocation
              </span>
              <h2 className="text-2xl xs:text-3xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-3 sm:mb-6">
                La foi devient une expérience vivante
              </h2>
              <div className="space-y-3 sm:space-y-4 text-slate-700 text-sm sm:text-lg leading-relaxed mb-5 sm:mb-8">
                <p>
                  Le mont Garizim occupe une place centrale dans l’histoire
                  biblique, symbole de bénédiction et de rencontre avec Dieu.
                  C’est sur cette montagne que le peuple d’Israël a proclamé les
                  paroles de vie et d’espérance, recevant la promesse d’une
                  destinée transformée par la puissance divine.
                </p>
                <p className="hidden sm:block">
                  Aujourd’hui, l’héritage du mont Garizim inspire notre
                  communauté à rechercher la présence de Dieu et à vivre sous
                  l’impact de Sa grâce.
                </p>
              </div>

              <div>
                <Button
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base shadow-lg transition-all hover:scale-[1.02] justify-center"
                  size="lg"
                  onClick={() => handleRouteChange("/events")}
                >
                  Actions de Grâce 2025 — 48e &rarr;
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unified Pastoral Leadership Gallery */}
      <section className="py-10 sm:py-24 bg-slate-50 border-y border-slate-200/70">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-16 space-y-2 sm:space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-[11px] sm:text-xs font-bold tracking-widest uppercase shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span>Direction Spirituelle</span>
            </div>
            <h2 className="text-2xl sm:text-5xl font-black text-slate-900 tracking-tight">
              Nos Pasteurs & Dirigeants
            </h2>
            <p className="text-slate-600 text-xs sm:text-lg leading-relaxed">
              Une équipe pastorale dévouée au service de Dieu, guidant notre
              assemblée avec amour, sagesse et engagement fraternel.
            </p>
          </div>

          {/* 4-Card Unified Leadership Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {/* 1. Pasteur Serge Joseph */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="aspect-[4/3] sm:aspect-[4/5] rounded-2xl overflow-hidden mb-4 sm:mb-6 bg-slate-100 border border-slate-100 shadow-inner">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/pasteur-femme.png?updatedAt=1760916546786"
                    alt="Pasteur Serge Joseph et son épouse"
                    width={400}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="mb-1.5 sm:mb-2">
                  <span className="inline-block px-2.5 py-0.5 sm:py-1 rounded-md bg-amber-100/80 text-amber-900 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider">
                    Pasteur Fondateur
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mb-2 sm:mb-3">
                  Pasteur Serge Joseph
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3 sm:line-clamp-6">
                  Animé par une passion profonde pour servir Dieu et sa
                  communauté, il a fondé Garizim avec une vision claire : offrir
                  un lieu d’accueil, de prière et de transformation pour tous.
                  Grâce à son leadership inspirant, Garizim est devenu un
                  symbole vivant de foi et de solidarité.
                </p>
              </div>
            </div>

            {/* 2. Pasteur Othniel Joseph */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="aspect-[4/3] sm:aspect-[4/5] rounded-2xl overflow-hidden mb-4 sm:mb-6 bg-slate-100 border border-slate-100 shadow-inner">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/othniel.jpg?updatedAt=1760896561970"
                    alt="Pasteur Othniel Joseph"
                    width={400}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="mb-1.5 sm:mb-2">
                  <span className="inline-block px-2.5 py-0.5 sm:py-1 rounded-md bg-slate-900 text-white text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider">
                    Pasteur Titulaire
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mb-2 sm:mb-3">
                  Pasteur Othniel Joseph
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3 sm:line-clamp-6">
                  Engagé à accompagner chaque croyant dans une aventure de foi
                  authentique, de partage et de croissance spirituelle.
                  Ensemble, nous découvrons la puissance de l’amour de Dieu et
                  la richesse de la fraternité chrétienne.
                </p>
              </div>
            </div>

            {/* 3. Pasteur Serge Joseph Jr. */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="aspect-[4/3] sm:aspect-[4/5] rounded-2xl overflow-hidden mb-4 sm:mb-6 bg-slate-100 border border-slate-100 shadow-inner">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/serge-junior.jpg?updatedAt=1760896562157"
                    alt="Pasteur Serge Joseph Jr."
                    width={400}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="mb-1.5 sm:mb-2">
                  <span className="inline-block px-2.5 py-0.5 sm:py-1 rounded-md bg-sky-100 text-sky-900 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider">
                    Pasteur Associé
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mb-2 sm:mb-3">
                  Pasteur Serge Joseph Jr.
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3 sm:line-clamp-6">
                  C'est avec une immense joie que nous vous accueillons au sein
                  de la communauté. Engagé à marcher à vos côtés sur le chemin
                  de la foi, de l'amour et de l'approfondissement des
                  enseignements bibliques.
                </p>
              </div>
            </div>

            {/* 4. Pasteur Jimmy Jeanty */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group">
              <div>
                <div className="aspect-[4/3] sm:aspect-[4/5] rounded-2xl overflow-hidden mb-4 sm:mb-6 bg-slate-100 border border-slate-100 shadow-inner">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/jimmy.jpg?updatedAt=1760896561686"
                    alt="Pasteur Jimmy Jeanty"
                    width={400}
                    height={500}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="mb-1.5 sm:mb-2">
                  <span className="inline-block px-2.5 py-0.5 sm:py-1 rounded-md bg-sky-100 text-sky-900 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider">
                    Pasteur Associé
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mb-2 sm:mb-3">
                  Pasteur Jimmy Jeanty
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3 sm:line-clamp-6">
                  Au service de cette belle communauté depuis de nombreuses
                  années, il veille avec affection pastorale à ce que chacun
                  trouve sa place, son soutien spirituel et la joie de servir le
                  Seigneur dans l'unité.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chants d'Espérance Feature Section */}
      <section className="py-10 sm:py-20 bg-slate-950 text-white relative overflow-hidden">
        {/* Background Ambient Glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-6 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full bg-slate-900 border border-sky-500/30 text-sky-400 text-[11px] sm:text-sm font-bold tracking-widest uppercase mb-3 sm:mb-4">
              <span>Hymnes & Louanges</span>
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 sm:mb-6 tracking-tight">
              Chants d'Espérance & Louanges
            </h2>
            <p className="text-slate-300 text-xs sm:text-lg leading-relaxed max-w-2xl mx-auto">
              Chaque cantique porte un souvenir. Retrouvez tous les recueils
            </p>
          </div>

          {/* Featured Random Song Preview Pill Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto mb-8 sm:mb-12">
            {loadingRandom &&
              [...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className={`p-4 sm:p-5 rounded-2xl bg-slate-900/80 border border-slate-800 animate-pulse h-24 sm:h-28 flex flex-col justify-between ${i >= 2 ? "hidden sm:flex" : ""}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="h-5 w-12 bg-slate-800 rounded" />
                    <div className="h-4 w-10 bg-slate-800 rounded" />
                  </div>
                  <div className="h-4 bg-slate-800 rounded w-4/5" />
                  <div className="h-3 bg-slate-800/60 rounded w-1/2" />
                </div>
              ))}

            {!loadingRandom && hasErrorRandom && (
              <div className="col-span-full text-center py-6 sm:py-8 px-4 rounded-2xl bg-slate-900/70 border border-slate-800">
                <p className="text-slate-300 text-xs sm:text-sm mb-3">
                  Impossible de charger la sélection des cantiques pour le
                  moment.
                </p>
                <button
                  onClick={fetchRandom}
                  className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sky-400 text-xs font-semibold border border-slate-700 transition-colors"
                >
                  Réessayer
                </button>
              </div>
            )}

            {!loadingRandom &&
              !hasErrorRandom &&
              randomHymns.map((song, idx) => {
                const badge =
                  song.section_code.split("-")[0] ||
                  song.section_code.substring(0, 2).toUpperCase();
                const langLabel = getLanguageLabel(song.language);

                return (
                  <div
                    key={song.id || `${song.section_code}-${song.number}`}
                    onClick={() => {
                      const slug = getHymnSlug(song.number, song.title);
                      handleRouteChange(
                        `/chants-desperance/${encodeURIComponent(song.section_code)}/${slug}`,
                      );
                    }}
                    className={`p-3.5 sm:p-5 rounded-2xl bg-slate-900/90 hover:bg-slate-850 border border-slate-800 hover:border-sky-500/50 transition-all duration-200 cursor-pointer text-left group shadow-lg hover:shadow-sky-500/10 flex flex-col justify-between ${idx >= 2 ? "hidden sm:flex" : ""}`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                        <span className="font-serifx font-black text-xs sm:text-sm text-sky-400 group-hover:text-sky-300">
                          {badge} - {song.section}
                        </span>
                        <span className="text-sm sm:text-xl font-bold px-2 py-0.5 rounded-md bg-slate-800 text-amber-300 border border-slate-700">
                          {song.number}
                        </span>
                      </div>
                      <h4 className="font-bold text-white text-sm sm:text-base line-clamp-2 group-hover:text-sky-200 transition-colors leading-snug">
                        {song.title}
                      </h4>
                    </div>
                    <div className="flex items-center justify-between mt-2 sm:mt-3 pt-1.5 sm:pt-2 border-t border-slate-800/80 text-[11px] sm:text-xs text-slate-400">
                      {langLabel && (
                        <span className="text-sky-300 font-semibold">
                          {langLabel}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button
              className="bg-white hover:bg-slate-100 text-slate-950 font-bold px-6 sm:px-8 py-3 sm:py-6 rounded-full text-xs sm:text-lg shadow-xl shadow-white/10 hover:scale-105 transition-all"
              onClick={() => handleRouteChange("/chants-desperance")}
            >
              PARCOURIR
            </Button>
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-20 bg-white border-t border-slate-200/80">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-[11px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-3">
              Prédications & Vie de l'Église
            </span>
            <h2 className="text-2xl sm:text-5xl font-black text-slate-900 mb-2 sm:mb-4 tracking-tight">
              Garizim Électrisant !
            </h2>
            <p className="text-xs sm:text-lg text-slate-600 leading-relaxed">
              Découvrez nos derniers sermons et plongez dans des messages
              inspirants qui nourrissent votre foi et votre esprit.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative w-full aspect-video bg-slate-900 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-slate-800">
              <iframe
                src="https://imagekit.io/player/embed/4apzi8lz0/egarizim/egarizim_Q3VPvdB4.mp4?controls=true&autoplay=false&loop=false&background=%23000000&updatedAt=1760896566635&thumbnail=https%3A%2F%2Fik.imagekit.io%2F4apzi8lz0%2Fegarizim%2Fegarizim_Q3VPvdB4.mp4%2Fik-thumbnail.jpg%3FupdatedAt%3D1760896566635&updatedAt=1760896566635"
                title="Garizim Électrisant"
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              />
            </div>
            <div className="text-center mt-6"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeGarizimSections;
